import { ConsentKeys, type Consents } from '@carvana/core-consent';
import List from '@carvana/ds-list';
import Button from '@carvana/ds-button';
import React, { useState } from 'react';
import { EventedModal, ModalNames } from '|/app/modals';
import { ConsentsSection } from '|/app/purchase/checkout/steps/review-order/cio/components/consents-section';
import { PageEventContextProvider } from '|/context';
import useEventHelper from '|/hooks/use-event-helper';
import { ReviewSummaryResponse } from '|/services/purchase-api';
import styles from './place-order-initial.module.css';

interface ConsentSheetProps {
  consentHtml: Consents;
  agreementStates: Record<ConsentKeys | string, boolean>;
  updateAgreement: (key: ConsentKeys | string, value: boolean) => void;
  shouldDisplayNRSF: boolean;
  shouldDisplayMarylandNRSF: boolean;
  isCarvanaFinanced: boolean;
  previouslyAdoptedConsents: Set<string>;
}

interface PlaceOrderInitialProps {
  onSubmit: () => void;
  hasConsents: boolean;
  areAgreementsComplete: () => boolean;
  consentProps: ConsentSheetProps;
  summary: ReviewSummaryResponse | null;
}

const PlaceOrderInitial: React.FC<PlaceOrderInitialProps> = ({
  onSubmit,
  hasConsents,
  areAgreementsComplete,
  consentProps,
  summary
}) => {
  const { sendCompleteEvent } = useEventHelper();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleContinue = () => {
    if (hasConsents) {
      setIsModalOpen(true);
    } else {
      onSubmit();
    }
  };

  const handleConsentSubmit = () => {
    sendCompleteEvent(true, {
      pageName: 'Final Details',
      pageSubName: 'Agreements',
      sectionName: 'Final Details'
    });
    setIsModalOpen(false);
    onSubmit();
  };

  return (
    <PageEventContextProvider
      value={{ pageName: 'Final Details', pageSubName: null, sectionName: 'Final Details' }}
    >
      <div className={styles.container}>
        <List variant="unordered" size="small">
          <li>
            We&apos;ll do a quick check to make sure everything&apos;s ready for your online
            purchase and registration.
          </li>
          <li>This usually takes about 15 seconds.</li>
          <li>
            If any additional steps are needed, we&apos;ll add reservation time to your order.
          </li>
        </List>
        <Button
          variant="primary"
          size="md"
          className={styles.cta}
          width="100%"
          onClick={handleContinue}
          data-analytics-id="verification-continue"
        >
          Continue
        </Button>

        <EventedModal
          isOpen={isModalOpen}
          analyticsId={ModalNames.VerificationAgreements}
          onClose={() => setIsModalOpen(false)}
          contentPadding={false}
          divLine={false}
          aria-label="Agreements"
        >
          <PageEventContextProvider
            value={{
              pageName: 'Final Details',
              pageSubName: 'Agreements',
              sectionName: 'Final Details'
            }}
          >
            <div className={styles.consentBody}>
              <ConsentsSection
                consentHtml={consentProps.consentHtml}
                shouldDisplayNRSF={consentProps.shouldDisplayNRSF}
                shouldDisplayMarylandNRSF={consentProps.shouldDisplayMarylandNRSF}
                isCarvanaFinanced={consentProps.isCarvanaFinanced}
                summary={summary}
                agreementStates={consentProps.agreementStates}
                updateAgreement={consentProps.updateAgreement}
                previouslyAdoptedConsents={consentProps.previouslyAdoptedConsents}
              />
              <Button
                variant="primary"
                size="md"
                disabled={!areAgreementsComplete()}
                onClick={handleConsentSubmit}
                data-analytics-id="verification-consent-submit"
                className={styles.cta}
                width="100%"
              >
                Continue
              </Button>
            </div>
          </PageEventContextProvider>
        </EventedModal>
      </div>
    </PageEventContextProvider>
  );
};

export default PlaceOrderInitial;
